import { keepPreviousData, skipToken, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'

import { ApiError, ignoreAlreadyGone } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error-message'
import { usersGridContract, useDataGrid } from '@/modules/data-grid'
import { usersApi } from '../../api/users-api'
import { userKeys, iamInvalidates, assistantEffectMeta } from '../../api/query-keys'

export function useUserCandidates(
  entityId: string,
  options?: { enabled?: boolean; query?: string },
) {
  const enabled = options?.enabled ?? true
  const query = options?.query?.trim() ?? ''
  return useInfiniteQuery({
    queryKey: userKeys.candidates(entityId, query),
    queryFn: enabled
      ? ({ pageParam }) => usersApi.getUserCandidates(entityId, { after: pageParam || undefined, query })
      : skipToken,
    initialPageParam: '',
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo.hasNextPage ? (lastPage.pageInfo.endCursor ?? undefined) : undefined,
    meta: assistantEffectMeta,
  })
}

export function useEntityUsers(
  entityId: string | null,
  search = '',
  includeDeactivated = false,
  options: { keepPreviousResults?: boolean } = {},
) {
  const query = search.trim()
  return useInfiniteQuery({
    queryKey: userKeys.list(entityId ?? '', query, includeDeactivated),
    placeholderData: options.keepPreviousResults ? keepPreviousData : undefined,
    queryFn:
      entityId === null
        ? skipToken
        : ({ pageParam }) => usersApi.getEntityUsers(entityId, { after: pageParam || undefined, query, includeDeactivated }),
    initialPageParam: '',
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo.hasNextPage ? (lastPage.pageInfo.endCursor ?? undefined) : undefined,
    meta: assistantEffectMeta,
  })
}

export function useUsersGrid(entityId: string | null) {
  return useDataGrid({
    contract: usersGridContract,
    queryKey: [...userKeys.list(entityId ?? ''), 'grid'],
    endpoint: `/api/v1/entities/${entityId ?? ''}/users`,
    enabled: entityId !== null,
    meta: assistantEffectMeta,
  })
}

export function useUserAccessProfile(
  entityId: string | null,
  userId: string | null,
  serviceAccount = false,
) {
  return useQuery({
    queryKey: userKeys.accessProfile(entityId ?? '', userId ?? '', serviceAccount),
    queryFn:
      entityId === null || userId === null
        ? skipToken
        : () => serviceAccount
            ? usersApi.getServiceAccountAccessProfile(entityId, userId)
            : usersApi.getUserAccessProfile(entityId, userId),
    retry: (count, error) => !(error instanceof ApiError) && count < 1,
    meta: assistantEffectMeta,
  })
}

export function useRevokeUserAccess() {
  return useMutation({
    mutationFn: ({ entityId, userId }: { entityId: string; userId: string }) =>
      ignoreAlreadyGone(() => usersApi.deleteUserAccess(entityId, userId)),
    meta: { invalidates: iamInvalidates() },
  })
}

export function useAssignUserRole(serviceAccount = false) {
  return useMutation({
    mutationFn: ({
      entityId,
      userId,
      roleId,
    }: {
      entityId: string
      userId: string
      roleId: string
    }) => serviceAccount
      ? usersApi.assignServiceAccountRole(entityId, userId, roleId)
      : usersApi.assignUserRole(entityId, userId, roleId),
    meta: { invalidates: iamInvalidates() },
  })
}

const ASSIGN_USER_ROLE_ERRORS: Record<string, string> = {
  ROLE_NOT_ASSIGNABLE: 'That role can’t be assigned at this entity.',
}

export function assignUserRoleErrorMessage(error: unknown) {
  return apiErrorMessage(error, 'Couldn’t assign the role. Try again.', {
    codes: ASSIGN_USER_ROLE_ERRORS,
  })
}

export function useRevokeUserRole(serviceAccount = false) {
  return useMutation({
    mutationFn: ({
      entityId,
      userId,
      roleId,
    }: {
      entityId: string
      userId: string
      roleId: string
    }) => ignoreAlreadyGone(() => serviceAccount
      ? usersApi.revokeServiceAccountRole(entityId, userId, roleId)
      : usersApi.revokeUserRole(entityId, userId, roleId)),
    meta: { invalidates: iamInvalidates() },
  })
}
